import { useState } from "react";
import { createPortal } from "react-dom";
import { LOGO_B64 } from "./SalesReports";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";
import { loadCompanyProfile } from "./CompanyProfile";
import { SWEETLEAF_LOGO_B64 } from "./brandLogos";
import { derivePalletStatus, resolvePendingStage, invoicesForPallet, twinRiverPalletStatus, buildSlPassingIndex } from "./saleStatusCalc";

/* ═══════════════════════════════════════════════════════════════════════════
   Payment Confirmation
   Shared utility used by BillRegistry, InvoiceRegistry, ExpenseBillRegistry
   to generate a printable confirmation document right after a payment or
   deposit is recorded. Mirrors the SalesReports navy-#1B3A4B style.
   ═══════════════════════════════════════════════════════════════════════════ */

function fmtMoney(n) {
  var v = parseFloat(n) || 0;
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/* Shared filename builder for the downloadable PDF — "payment" (bill
   payments, expense payments) vs "deposit" (invoice deposits) pick the
   matching prefix; partyName/date fill in the rest. Single source so
   every caller of PaymentConfirmationPreview (BillRegistry,
   ExpenseBillRegistry, InvoiceRegistry, SweetLeafPortal, PaymentRegistry)
   gets the same naming without duplicating the logic. */
export function paymentConfirmationFilename(kind, partyName, date) {
  var prefix = kind === "deposit" ? "Deposit Confirmation" : "Payment Confirmation";
  return safeFilename(prefix + " " + (partyName || "") + " " + (date || "")) + ".pdf";
}
function fmtDateLong(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

var GFI = {
  name: "Ground Fresh International, Inc.",
  lines: ["152 W Hoffman Ave Ste 5", "Lindenhurst, NY 11757", "USA"],
};

/* SweetLeaf-branded documents (2026-09-25, Arturo: "los pagos que efectuo
   de four seasons a sweetleaf generan el pdf con la informacion y logo
   de ground-fresh, debe ser sweetleaf") — party.brand === "sl", set by
   the SweetLeaf module's callers. Same address as brandConfig("sl") in
   SweetLeafPortal.js. */
var SL_ENTITY = {
  name: "Sweetleaf Produce Inc.",
  lines: ["152 W Hoffman Ave Ste. 5", "Lindenhurst, NY 11757-4041", "USA"],
};

function escHtml(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/* Fallbacks — only used when Company Profile's own fields (editable in
   CompanyProfile.js > "Market Conditions Notice" / "Protection Notice")
   are empty/unset. */
var DEFAULT_MARKET_NOTICE_EN = "MARKET CONDITIONS NOTICE: Payments and returns shown in this confirmation are subject to prevailing market conditions. Sudden market fluctuations, demand changes, or unforeseen events may affect final settlement amounts. Ground Fresh International Inc. does not guarantee that current amounts will match final settlements in cases of significant market volatility.";
var DEFAULT_MARKET_NOTICE_ES = "AVISO DE CONDICIONES DE MERCADO: Los pagos y retornos mostrados en esta confirmación están sujetos a las condiciones de mercado vigentes. Fluctuaciones repentinas del mercado, cambios en la demanda, o eventos imprevistos pueden afectar los montos finales de liquidación. Ground Fresh International Inc. no garantiza que los montos actuales coincidan con las liquidaciones finales en casos de volatilidad significativa del mercado.";
var DEFAULT_PROTECTION_NOTICE_EN = "PROTECTION NOTICE: Lots or pallets marked as 'Protection' or 'PAS' status are subject to additional price adjustment. The original sale price is not guaranteed as the final return — actual settlements may reflect deductions based on quality issues or agreed terms.";
var DEFAULT_PROTECTION_NOTICE_ES = "AVISO DE PROTECCIÓN: Los lotes o pallets marcados con estatus 'Protección' o 'PAS' están sujetos a ajustes de precio adicionales. El precio de venta original no se garantiza como retorno final — las liquidaciones reales pueden reflejar deducciones basadas en problemas de calidad o términos acordados.";

/**
 * @param {Object} payment   { id, paymentDate, type, paymentMethod, reference, notes }
 * @param {Object} party     { name, lines: [string,...] }
 * @param {Array}  rows      [{ invoiceDate, reference, invoiceTotal, amountPaid, stillOwing,
 *                              productStatus: [{ product, status, cases }] (live per-pallet
 *                              breakdown, grouped by product+status — [] for expense rows),
 *                              previousPayments: [{ date, amount, method?, reference? }] }]
 * @param {string} lang      "en" | "es" — ONLY affects the Status column header and the two
 *                            Company-Profile-editable legal notices at the foot of the document;
 *                            everything else in this generated document stays English per
 *                            CLAUDE.md's "documents for clients: always English" rule.
 */
export function buildPaymentConfirmationHTML(payment, party, rows, lang) {
  var isEs = lang === "es";
  var totalPaid = (rows || []).reduce(function (s, r) { return s + (parseFloat(r.amountPaid) || 0); }, 0);
  var isInbound = payment.type === "inbound";
  /* Title is fixed per direction — it never flips to "Advance Payment"
     even when every row in the batch is an advance. Concept is a
     per-row property (badges below), not a document-level one. */
  var docType = isInbound ? "Deposit Confirmation" : "Payment Confirmation";
  var isSl = party.brand === "sl";
  var ME = isSl ? SL_ENTITY : GFI;
  var fromParty = isInbound ? party : ME;
  var toParty = isInbound ? ME : party;

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + docType + ' - ' + payment.id + '</title><style>';
  h += '@page{size:letter;margin:0.6in 0.7in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #1B3A4B}';
  /* Bumped to 100 — matches other GFI docs so "International" reads. */
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:26px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:11px;color:#666;text-align:right;margin-top:4px;font-family:"Courier New",monospace}';
  h += '.parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:14px 0}';
  h += '.party{padding:10px 14px;border:1px solid #e0e0e0;border-radius:6px;background:#fafbfc}';
  h += '.party .p-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.party .p-name{font-size:13px;font-weight:700;color:#1B3A4B;margin-bottom:2px}';
  h += '.party .p-addr{font-size:9px;color:#666;line-height:1.6}';
  h += '.meta-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin:0 0 12px;padding:12px 16px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B;border-right:4px solid #1B3A4B}';
  h += '.meta-grid .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.total-paid{margin:10px 0 16px;padding:14px 18px;background:#1B3A4B;color:#fff;border-radius:6px;display:flex;justify-content:space-between;align-items:center}';
  h += '.total-paid .tp-label{font-size:10px;text-transform:uppercase;letter-spacing:2px;font-weight:600}';
  h += '.total-paid .tp-value{font-size:22px;font-weight:800;font-family:"Courier New",monospace}';
  h += 'table{width:100%;border-collapse:collapse}';
  /* Compact table (fix-dashboard.md FIX 1) — smaller/tighter than the
     original 7px/8px th and 8px/12px/10px td paddings+sizes, which let
     long References (e.g. "LOT 58045 · GF-003 · TA-A 20040") wrap to a
     2nd line and made batches blow past 10-12 rows per letter page. */
  h += '.items th{padding:5px 8px;font-size:7.5px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#1B3A4B;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:4px 8px;border-bottom:1px solid #eee;font-size:9.5px;vertical-align:middle}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace;font-size:10px}';
  h += '.section-title{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#1B3A4B;font-weight:700;margin:18px 0 8px;padding-bottom:4px;border-bottom:2px solid #1B3A4B}';
  /* Badges — orange for Advance, green for Balance, red for a non-
     goodDelivery Status (fix-dashboard.md FIX 2) — same .badge shape
     as the other two, just its own color pair + slightly larger
     font-size per spec. */
  h += '.badge{display:inline-block;padding:2px 8px;border-radius:10px;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px}';
  h += '.badge-advance{background:#FFF3E0;color:#E65100}';
  h += '.badge-balance{background:#E8F5E9;color:#2E7D32}';
  h += '.badge-status{background:#FEE2E2;color:#B91C1C;font-size:9.5px;margin:1px}';
  /* Product Status Detail table (2026-10-30, Arturo — see note by
     productStatusDetail() below) — its own badge colors per status,
     matching the app's own SALE_STATUS_COLORS palette (BillRegistry.js)
     so a reader already familiar with the app's colors recognizes them
     here: green=Good Delivery, red=Protection, amber=PAS. */
  h += '.badge-gd{background:rgba(16,185,129,0.12);color:#059669}';
  h += '.badge-prot{background:rgba(239,68,68,0.12);color:#DC2626}';
  h += '.badge-pas{background:rgba(245,158,11,0.12);color:#D97706}';
  h += '.psd-table{margin-top:8px}';
  h += '.psd-table th{padding:4px 8px;font-size:7.5px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#546E7A;font-weight:600}';
  h += '.psd-table td{padding:4px 8px;border-bottom:1px solid #eee;font-size:9.5px}';
  /* Principal rows (one per bill/invoice) render at normal size;
     sub-rows (Previous Activity / Current Payment / Remaining
     Balance) are indented and smaller so the grouping reads as a
     mini-ledger under each principal line. Sizes trimmed alongside
     .items td above (12px -> 11px, 10px -> 9.5px) for the same
     compacting pass. */
  h += '.items tr.principal-row td{font-size:11px;font-weight:500}';
  h += '.items tr.sub-row td{font-size:9.5px;color:#555;padding-left:24px}';
  h += '.items tr.sub-row.remaining td{font-weight:700;color:#1a1a1a;border-top:1px solid #ddd}';
  h += '.notes-box{font-size:10px;color:#666;line-height:1.5;white-space:pre-wrap;padding:10px 14px;background:#fafbfc;border-radius:6px;border-left:3px solid #1B3A4B;border-right:3px solid #1B3A4B}';
  h += '.footer{margin-top:24px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  /* Force a 14px top buffer on the body so the document header (logo +
     title) NEVER gets clipped at the top of the iframe / print preview.
     The previous layout had `*{margin:0;padding:0}` zeroing this out,
     and some browsers (Chrome srcDoc, mobile Safari) render the initial
     iframe scrollTop at a non-zero position when the doc is taller
     than the iframe — which trimmed the top of the logo. The padding
     gives a visible safety margin and the inline script below
     hard-resets the scroll position on load. */
  h += 'body{padding-top:14px}';
  h += '</style></head><body><div class="page">';
  /* Defensive scroll reset for the iframe preview. Print itself
     ignores scroll position (the browser repaginates), so this only
     affects the on-screen preview where srcDoc init scroll is
     inconsistent across browsers. */
  h += '<script>try{window.scrollTo(0,0);document.documentElement.scrollTop=0;document.body.scrollTop=0;}catch(e){}</script>';

  /* Header */
  h += '<div class="header"><div class="logo"><img src="' + (isSl ? SWEETLEAF_LOGO_B64 : LOGO_B64) + '" /></div>';
  h += '<div><div class="doc-title">' + docType.toUpperCase() + '</div>';
  h += '<div class="doc-sub">' + (payment.id || "") + ' · ' + fmtDateLong(payment.paymentDate) + '</div></div></div>';

  /* From / To */
  h += '<div class="parties">';
  h += '<div class="party"><div class="p-label">From</div><div class="p-name">' + (fromParty.name || "—") + '</div>';
  if (fromParty.lines && fromParty.lines.length) h += '<div class="p-addr">' + fromParty.lines.join("<br/>") + '</div>';
  h += '</div>';
  h += '<div class="party"><div class="p-label">To</div><div class="p-name">' + (toParty.name || "—") + '</div>';
  if (toParty.lines && toParty.lines.length) h += '<div class="p-addr">' + toParty.lines.join("<br/>") + '</div>';
  h += '</div></div>';

  /* Meta grid */
  var methodLabel = payment.paymentMethod ? payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1) : "—";
  h += '<div class="meta-grid">';
  h += '<div><div class="label">Payment Date</div><div class="value">' + fmtDateLong(payment.paymentDate) + '</div></div>';
  h += '<div><div class="label">Method</div><div class="value">' + methodLabel + '</div></div>';
  h += '<div><div class="label">Reference</div><div class="value">' + (payment.reference || "—") + '</div></div>';
  /* 2026-09-15 — Arturo: "para esos pagos [expense bills] agrega al
     momento de hacerlo debe tener fecha de procesamiento, fecha de
     delivery... y mostrar estos datos en el pdf" — captured on the
     Batch Payment form (ExpenseBillRegistry.js) and saved on the
     payment record; shown here only when present (expense-bill
     payments only, per how they're captured — a Bill/Invoice payment
     never sets these fields). */
  if (payment.processingDate) h += '<div><div class="label">' + (isEs ? "Fecha de Procesamiento" : "Processing Date") + '</div><div class="value">' + fmtDateLong(payment.processingDate) + '</div></div>';
  if (payment.deliveryDate) h += '<div><div class="label">' + (isEs ? "Fecha de Entrega" : "Delivery Date") + '</div><div class="value">' + fmtDateLong(payment.deliveryDate) + '</div></div>';
  h += '</div>';

  /* Total */
  h += '<div class="total-paid"><div class="tp-label">Total ' + (isInbound ? "Received" : "Paid") + '</div>';
  h += '<div class="tp-value">$' + fmtMoney(totalPaid) + ' USD</div></div>';

  /* Unified listing — one section per allocation row, no split
     advance/balance tables. Advance rows render as a single line
     (Date/Reference/Amount + badge) and never disclose Bill Total or
     remaining balance. Balance rows render a principal line (Ref +
     Bill Total + badge) followed by indented sub-rows: one "Previous
     Activity" line per historical payment against that bill
     (ascending by date, tagged with its own concept), a "Current
     Payment" line for this batch's amount, and a bold "Remaining
     Balance" line computed as Total − prior payments − this payment.
     Rows for the same bill share a background tint so the grouping
     reads visually even though it's several <tr>s. */
  var totalLabel = isInbound ? "Invoice Total" : "Bill Total";
  var statusHeader = isEs ? "Estatus" : "Status";
  /* 2026-09-15 — Arturo: "los pagos de expense bills no deben llevar
     la columna status porque no es fruta" — Status shows the
     Protection/PAS delivery condition, which only ever applies to
     fruit (Bill/Invoice) lines; expense-bill lines have no such
     concept. Column dropped entirely for an expense party instead of
     just left empty. Reference absorbs the freed width.

     2026-10-30 — Arturo, Terey Avocados payment confirmation with
     Blackberry protected + Blueberry good delivery on the SAME bill:
     "muestra en el payment 'Protegido' debo corregir eso... podria no
     ser toda la carga protegida". A single per-BILL status pill
     (the old `r.saleStatus` badge below) necessarily picked ONE value
     to represent every product line on that bill — correct when the
     whole bill shares one status, misleading the instant it mixes
     (the grower reads "Protection" and assumes 100% of the load is
     under dispute, when only one product/pallet actually is). The
     per-bill Status column is removed entirely now; in its place,
     "Pallet Status Detail" below breaks the SAME information down by
     product + case count, computed live from the real pallets (see
     productStatusDetail()) — exactly what Arturo asked for: "por
     cajas haciendo la suma de los pallet detailed". */
  var advanceRefs = [];
  var anyNonGoodDelivery = false;
  if (party.isExpense) {
    /* 2026-09-15 — Arturo: "los expense bills no los hago en pagos,
       generalmente es en un solo pago, no necesito varias lineas de
       descripcion por factura, en una sola linea debe mostrar bill
       total, less payments, balance y ya no mas... y el tipo de pago,
       en expense bills no es necesario" — completely separate,
       simpler table for this party: ONE row per bill (Date, Reference,
       Bill Total, Less Payments, Balance), never the multi-row
       Previous-Activity/Current-Payment/Remaining-Balance breakdown
       the Bill/Invoice table below uses — that breakdown exists for
       the partial/installment payments fruit settlements often need;
       expense bills are almost always paid in one shot. No Amount,
       Type, or Status columns. */
    h += '<table class="items"><thead><tr>';
    h += '<th class="c" style="width:12%">Date</th>';
    /* 2026-09-15 — Arturo: "reference de la linea de pago debe decir
       Invoice Number" — this column always held the vendor's own
       invoice number (bill.reference, see buildExpenseRef); the
       header text now says so plainly instead of the generic
       "Reference" label the Bill/Invoice table still uses. */
    h += '<th style="text-align:left;width:40%">' + (isEs ? "Número de Factura" : "Invoice Number") + '</th>';
    h += '<th class="r" style="width:16%">' + totalLabel + '</th>';
    h += '<th class="r" style="width:16%">Less Payments</th>';
    h += '<th class="r" style="width:16%">Balance</th>';
    h += '</tr></thead><tbody>';
    (rows || []).forEach(function (r, gi) {
      var bg = gi % 2 === 1 ? ' style="background:#fafbfc"' : '';
      var prevSum = (r.previousPayments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var lessPayments = prevSum + (parseFloat(r.amountPaid) || 0);
      var remaining = (parseFloat(r.invoiceTotal) || 0) - lessPayments;
      var remainingStr = (remaining < 0 ? "-$" + fmtMoney(-remaining) : "$" + fmtMoney(remaining));
      h += '<tr class="principal-row"' + bg + '>';
      h += '<td class="c">' + fmtDateShort(r.invoiceDate) + '</td>';
      h += '<td>' + (r.reference || "—") + '</td>';
      h += '<td class="r mono">$' + fmtMoney(r.invoiceTotal) + '</td>';
      h += '<td class="r mono">-$' + fmtMoney(lessPayments) + '</td>';
      h += '<td class="r mono"><b>' + remainingStr + '</b></td>';
      h += '</tr>';
    });
    h += '</tbody></table>';
  } else {
  h += '<table class="items"><thead><tr>';
  h += '<th class="c" style="width:10%">Date</th>';
  h += '<th style="text-align:left;width:48%">Reference</th>';
  h += '<th class="r" style="width:13%">' + totalLabel + '</th>';
  h += '<th class="r" style="width:15%">Amount</th>';
  h += '<th class="c" style="width:14%">Type</th>';
  h += '</tr></thead><tbody>';
  anyNonGoodDelivery = (rows || []).some(function (r) { return (r.productStatus || []).some(function (ps) { return isProtectionStatus(ps.status); }); });
  (rows || []).forEach(function (r, gi) {
    var bg = gi % 2 === 1 ? ' style="background:#fafbfc"' : '';
    var isRowAdvance = r.concept === "advance";
    if (isRowAdvance) {
      advanceRefs.push(r.reference || "—");
      h += '<tr class="principal-row"' + bg + '>';
      h += '<td class="c">' + fmtDateShort(payment.paymentDate) + '</td>';
      h += '<td>' + (r.reference || "—") + '</td>';
      h += '<td class="r mono">—</td>';
      h += '<td class="r mono"><b>$' + fmtMoney(r.amountPaid) + '</b></td>';
      h += '<td class="c"><span class="badge badge-advance">Advance</span></td>';
      h += '</tr>';
      return;
    }
    h += '<tr class="principal-row"' + bg + '>';
    h += '<td class="c">' + fmtDateShort(r.invoiceDate) + '</td>';
    h += '<td>' + (r.reference || "—") + '</td>';
    h += '<td class="r mono">$' + fmtMoney(r.invoiceTotal) + '</td>';
    h += '<td class="r mono">—</td>';
    h += '<td class="c"><span class="badge badge-balance">Balance</span></td>';
    h += '</tr>';
    var prevSum = 0;
    (r.previousPayments || [])
      .slice()
      .sort(function (a, b) { return String(a.date || "").localeCompare(String(b.date || "")); })
      .forEach(function (p) {
        var amt = parseFloat(p.amount) || 0;
        prevSum += amt;
        var tag = p.concept === "advance" ? " (Advance)" : " (Balance)";
        h += '<tr class="sub-row"' + bg + '>';
        h += '<td class="c">' + fmtDateShort(p.date) + '</td>';
        h += '<td>Previous Activity' + tag + '</td>';
        h += '<td class="r mono">—</td>';
        h += '<td class="r mono">-$' + fmtMoney(amt) + '</td>';
        h += '<td></td>';
        h += '</tr>';
      });
    h += '<tr class="sub-row"' + bg + '>';
    h += '<td class="c">' + fmtDateShort(payment.paymentDate) + '</td>';
    h += '<td>Current Payment</td>';
    h += '<td class="r mono">—</td>';
    h += '<td class="r mono">-$' + fmtMoney(r.amountPaid) + '</td>';
    h += '<td></td>';
    h += '</tr>';
    var remaining = (parseFloat(r.invoiceTotal) || 0) - prevSum - (parseFloat(r.amountPaid) || 0);
    var remainingStr = (remaining < 0 ? "-$" + fmtMoney(-remaining) : "$" + fmtMoney(remaining));
    h += '<tr class="sub-row remaining"' + bg + '>';
    h += '<td></td>';
    h += '<td><b>Remaining Balance</b></td>';
    h += '<td class="r mono">—</td>';
    h += '<td class="r mono"><b>' + remainingStr + '</b></td>';
    h += '<td></td>';
    h += '</tr>';
  });
  h += '</tbody></table>';
  }

  /* Pallet Status Detail — product + case breakdown, computed from the
     real per-pallet data (see productStatusDetail() below), not one
     ambiguous pill at bill level. Only rendered when there's something
     worth clarifying (at least one bill in the batch has a real
     protection/PAS/partial pallet) — a batch that's 100% Good Delivery
     stays uncluttered. Lists EVERY product on a bill that needed this
     section, including its Good Delivery lines too, so the reader sees
     the full picture ("podria no ser toda la carga protegida") instead
     of only the flagged part. */
  var statusLabels = isEs
    ? { goodDelivery: "Entrega Buena", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial", pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega" }
    : { goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection", pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery" };
  var statusBadgeClass = function (s) {
    if (s === "goodDelivery") return "badge-gd";
    if (s === "pas") return "badge-pas";
    return "badge-prot"; // protection, partialProtection, and any pending fallback read as "not yet resolved"
  };
  var detailRows = (rows || []).filter(function (r) { return (r.productStatus || []).length > 0; });
  if (!party.isExpense && detailRows.length > 0) {
    h += '<div class="section-title">' + (isEs ? "Detalle de Status por Pallet" : "Pallet Status Detail") + '</div>';
    h += '<table class="items psd-table"><thead><tr>';
    h += '<th style="text-align:left;width:34%">Reference</th>';
    h += '<th style="text-align:left;width:30%">' + (isEs ? "Producto" : "Product") + '</th>';
    h += '<th class="c" style="width:20%">' + statusHeader + '</th>';
    h += '<th class="r" style="width:16%">' + (isEs ? "Cajas" : "Cases") + '</th>';
    h += '</tr></thead><tbody>';
    detailRows.forEach(function (r, gi) {
      var bg = gi % 2 === 1 ? ' style="background:#fafbfc"' : '';
      (r.productStatus || []).forEach(function (ps, pi) {
        h += '<tr' + bg + '>';
        h += '<td>' + (pi === 0 ? escHtml(r.reference || "—") : '') + '</td>';
        h += '<td>' + escHtml(ps.product) + '</td>';
        h += '<td class="c"><span class="badge ' + statusBadgeClass(ps.status) + '">' + escHtml(statusLabels[ps.status] || ps.status) + '</span></td>';
        h += '<td class="r mono">' + (ps.cases || 0) + '</td>';
        h += '</tr>';
      });
    });
    h += '</tbody></table>';
  }

  /* Legend — only printed when the batch includes at least one
     advance row, listing every advance bill/invoice so the reader
     knows which lines are not yet final settlements. */
  if (advanceRefs.length > 0) {
    var legend = (isInbound ? 'Advance deposits' : 'Advance payments')
      + ' in this confirmation apply to: ' + advanceRefs.join(", ")
      + '. Final settlement of each will be reflected in a subsequent payment.';
    h += '<div class="notes-box" style="margin-top:14px">' + legend + '</div>';
  }

  /* Notes */
  if (payment.notes) {
    h += '<div class="section-title">Notes</div>';
    h += '<div class="notes-box">' + payment.notes + '</div>';
  }

  /* Market Conditions Notice — shown on every payment confirmation
     EXCEPT expense-bill (service) payments (2026-09-15, Arturo: "los
     expense bill son pagos por servicios, no es necesario que los pdf
     printouts lleven esta leyenda porque no se paga fruta" — the
     notice exists to caveat FRUIT settlement amounts against market
     volatility; a warehouse/customs/service payment has no such
     variability). Editable in Company Profile; falls back to the
     default text when the field is empty/unset. Language follows the
     active UI language at generation time (the one exception to this
     document's otherwise-English-always content — see the JSDoc note
     on `lang` above). Uses .notes-box — same left-bar/tint treatment as
     the Advance Notice above and the Payment Date meta-grid, per
     fix-dashboard.md FIX 3 (reused, not a new style). */
  var companyProfile = loadCompanyProfile();
  if (!party.isExpense) {
    var marketNotice = isEs
      ? (companyProfile.marketConditionsNoticeEs || DEFAULT_MARKET_NOTICE_ES)
      : (companyProfile.marketConditionsNoticeEn || DEFAULT_MARKET_NOTICE_EN);
    h += '<div class="notes-box" style="margin-top:14px">' + escHtml(marketNotice) + '</div>';
  }

  /* Protection Notice — ONLY when at least one row carries a non-
     goodDelivery saleStatus (Protection, PAS, etc. — same `saleStatus`
     array computed per row above for the Status column). Sits below
     Market Conditions with its own top margin so the two read as
     separate blocks even though both are .notes-box. */
  if (anyNonGoodDelivery) {
    var protectionNotice = isEs
      ? (companyProfile.protectionNoticeEs || DEFAULT_PROTECTION_NOTICE_ES)
      : (companyProfile.protectionNoticeEn || DEFAULT_PROTECTION_NOTICE_EN);
    h += '<div class="notes-box" style="margin-top:10px">' + escHtml(protectionNotice) + '</div>';
  }

  /* Footer */
  h += '<div class="footer">' + (isSl ? 'Sweetleaf Produce Inc. · 152 W Hoffman Ave Ste. 5, Lindenhurst, NY 11757' : 'Ground Fresh International, Inc. · 152 W Hoffman Ave Ste 5, Lindenhurst, NY 11757 · ground-fresh.com') + '</div>';
  h += '</div></body></html>';
  if (isSl) {
    /* Brand swap: SweetLeaf teal accent + entity name inside the legal notices. */
    h = h.split("#1B3A4B").join("#2A7A6F").split("Ground Fresh International Inc.").join("Sweetleaf Produce Inc.");
  }
  return h;
}

/* Promise<Blob> builder — reuses buildPaymentConfirmationHTML + the
   shared htmlToPdfBlob, same pattern as SalesReports.js's
   buildSalesReportBlob (fix-dashboard.md Fase 2 Parte B CAMBIO 2).
   Side-effect-free: no download, no window. Both the download button
   below and the future EmailPaymentConfirmationModal (PaymentRegistry.js)
   build on this exact function so the PDF is byte-identical either way. */
export function buildPaymentConfirmationBlob(payment, party, rows, lang) {
  var html = buildPaymentConfirmationHTML(payment, party, rows, lang);
  return htmlToPdfBlob(html);
}

/* React preview modal: shows the HTML in an iframe + Print / Close buttons. */
export function PaymentConfirmationPreview({ payment, party, rows, lang, onClose }) {
  var html = buildPaymentConfirmationHTML(payment, party, rows, lang);
  var printDoc = function () {
    var w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(function () { w.print(); }, 400);
  };
  var [pdfBusy, setPdfBusy] = useState(false);
  var handleDownloadPdf = async function () {
    setPdfBusy(true);
    try {
      var blob = await buildPaymentConfirmationBlob(payment, party, rows, lang);
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = paymentConfirmationFilename(payment.type === "inbound" ? "deposit" : "payment", party.name, payment.paymentDate);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF error: " + (e.message || String(e)));
    } finally {
      setPdfBusy(false);
    }
  };
  var docLabel = payment.type === "inbound" ? "Deposit" : "Payment";
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 20001, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 60, overflowY: "auto" }}>
      <div style={{ width: 800, maxWidth: "97vw", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden", marginBottom: 40 }}>
        <div style={{ padding: "10px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{docLabel} Confirmation — {payment.id}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={printDoc} style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Print / Save PDF</button>
            <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #1B3A4B", background: "transparent", color: "#1B3A4B", cursor: pdfBusy ? "wait" : "pointer", fontSize: 12, fontWeight: 600 }}>{pdfBusy ? "…" : "💾 Download PDF"}</button>
            <button onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>Close</button>
          </div>
        </div>
        <div style={{ padding: 0, background: "#fff" }}>
          {/* iframe sized to most of the viewport (not a fixed 720px)
              so the document's full top — logo + doc title — is
              always visible in the preview. onLoad re-forces scroll
              to 0 in case the browser's srcDoc init left the doc
              scrolled. */}
          <iframe
            title="payment-confirmation"
            srcDoc={html}
            onLoad={function (e) { try { var w = e.target.contentWindow; if (w) { w.scrollTo(0, 0); } } catch (err) {} }}
            style={{ width: "100%", height: "calc(100vh - 110px)", minHeight: 500, border: "none", display: "block" }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Helpers each registry can use to build the (party, rows) tuple.
   They read fresh state from localStorage so a confirmation always reflects
   what was just persisted.
   ═══════════════════════════════════════════════════════════════════════════ */

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }

function calcBillSubtotal(bill) {
  var grossSales = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      grossSales += (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    }
  });
  var totalFees = 0;
  (bill.fees || []).forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var commAmt = grossSales * ((parseFloat(bill.commissionRate) || 0) / 100);
  return grossSales - commAmt - totalFees;
}

function calcInvoiceTotal(inv) {
  var total = 0;
  (inv.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    }
  });
  var tFees = 0;
  (inv.fees || []).forEach(function (fe) { tFees += parseFloat(fe.amount) || 0; });
  return total - tFees;
}

function buildBillRef(bill) {
  var parts = [];
  if (bill.lot) parts.push("LOT " + bill.lot);
  if (bill.proforma) parts.push(bill.proforma);
  if (bill.shipperInvoice) parts.push(bill.shipperInvoice);
  return parts.join(" · ");
}
function buildInvRef(inv) {
  var parts = [];
  if (inv.puNumber) parts.push("PU " + inv.puNumber);
  if (inv.lot) parts.push("LOT " + inv.lot);
  if (inv.clientRef) parts.push(inv.clientRef);
  return parts.join(" · ");
}
/* 2026-09-15 — Arturo: "la referencia de pago debe ser unicamente el
   numero de factura nada mas" — this used to concatenate the internal
   EXP-#### id and the entry number alongside the actual vendor invoice
   number (bill.reference), cluttering the Reference column with
   internal bookkeeping the vendor/reader doesn't need. */
function buildExpenseRef(bill) {
  return bill.reference || "";
}

function isProtectionStatus(s) {
  return s === "protection" || s === "pas" || s === "partialProtection";
}

/* 2026-10-30 — Arturo (Terey Avocados payment, Blackberry protected +
   Blueberry good delivery on the SAME bill): a single per-bill status
   pill can't represent a mixed load without misleading the reader into
   thinking the whole thing is under dispute. These two builders read
   the REAL per-pallet data (bill.palletDetail for a Bill, or
   invoice.palletAllocations for an Invoice/SL deposit — the same
   sources BillRegistry.js/InvoiceRegistry.js's own Pallet Detail
   tables already use) and group CASES by product + live status —
   "por cajas haciendo la suma de los pallet detailed", his own words.
   Live status computation mirrors those registries exactly:
   twinRiverPalletStatus() first (ClariFresh/Twin River shipments grade
   pallets from their own quality report), then the normal
   Invoice-driven derivePalletStatus()/a.saleStatus, then the
   pendingPickUp/pendingDelivery fallback — never a frozen snapshot. */
function billProductStatusDetail(bill, shipments, allInvoices, slPassingIndex) {
  var shp = (shipments || []).find(function (s) { return String(s.lot) === String(bill.lot); });
  var groups = {};
  var order = [];
  (bill.palletDetail || []).forEach(function (p) {
    if (!p || !p.palletNum) return;
    var status = (shp && twinRiverPalletStatus(shp, p.palletNum))
      || derivePalletStatus(bill.lot, p.palletNum, allInvoices)
      || resolvePendingStage(invoicesForPallet(bill.lot, p.palletNum, allInvoices), slPassingIndex)
      || "pendingPickUp";
    var product = p.productBase || p.productName || "—";
    var key = product + "|" + status;
    if (!groups[key]) { groups[key] = { product: product, status: status, cases: 0 }; order.push(key); }
    groups[key].cases += parseInt(p.casesPallet, 10) || 0;
  });
  return order.map(function (k) { return groups[k]; });
}
function invoiceProductStatusDetail(inv, shipments, slPassingIndex) {
  var shp = (shipments || []).find(function (s) { return String(s.lot) === String(inv.lot); });
  var groups = {};
  var order = [];
  (inv.palletAllocations || []).forEach(function (a) {
    if (!a || !a.palletNum) return;
    var status = (shp && twinRiverPalletStatus(shp, a.palletNum)) || a.saleStatus || resolvePendingStage([inv], slPassingIndex) || "pendingPickUp";
    var meta = shp ? (shp.palletManifest || []).find(function (p) { return String(p.palletNum) === String(a.palletNum); }) : null;
    var product = (meta && (meta.productBase || meta.productName)) || "—";
    var key = product + "|" + status;
    if (!groups[key]) { groups[key] = { product: product, status: status, cases: 0 }; order.push(key); }
    groups[key].cases += parseInt(a.cases, 10) || 0;
  });
  return order.map(function (k) { return groups[k]; });
}

function supplierAddressLines(sup) {
  if (!sup) return [];
  return [sup.address || "", sup.colony || "", [sup.city, sup.state].filter(Boolean).join(", "), sup.zip || "", sup.country || ""].filter(Boolean);
}
function clientAddressLines(c) {
  if (!c) return [];
  return [c.address || "", c.address2 || "", [c.city, c.state].filter(Boolean).join(", "), c.zip || "", c.country || ""].filter(Boolean);
}

/* Compose (party, rows) for an outbound Bill payment. */
export function partyAndRowsForBillPayment(payment) {
  var bills = loadLS("gfi-bills", []);
  var suppliers = loadLS("gfi-suppliers", []);
  var shipments = loadLS("gfi-shipments", []);
  var allInvoices = loadLS("gfi-invoices", []);
  var slPassingIndex = buildSlPassingIndex(loadLS("gfi-sl-invoices", []));
  var firstBill = bills.find(function (b) { return b.id === (payment.allocations[0] && payment.allocations[0].billId); });
  var sup = firstBill ? suppliers.find(function (s) { return String(s.id) === String(firstBill.supplierId); }) : null;
  var party = {
    name: payment.shipperName || (sup && (sup.fullName || sup.shortName || sup.name)) || "—",
    lines: supplierAddressLines(sup),
  };
  var rows = (payment.allocations || []).map(function (alloc) {
    var bill = bills.find(function (b) { return b.id === alloc.billId; });
    if (!bill) return null;
    var subtotal = calcBillSubtotal(bill);
    var paidNow = parseFloat(bill.amountPaid) || 0;
    var prev = (bill.payments || [])
      .filter(function (p) { return p.paymentId !== payment.id; })
      .map(function (p) { return { date: p.date, amount: p.amount, method: p.method, reference: p.reference, concept: p.concept || "" }; });
    return {
      invoiceDate: bill.billDate,
      reference: buildBillRef(bill),
      invoiceTotal: subtotal,
      amountPaid: alloc.amount,
      stillOwing: subtotal - paidNow,
      concept: alloc.concept || "",
      productStatus: billProductStatusDetail(bill, shipments, allInvoices, slPassingIndex),
      previousPayments: prev,
    };
  }).filter(Boolean);
  return { party: party, rows: rows };
}

/* Compose (party, rows) for an inbound Invoice deposit.
   `opts.invoices` overrides the default GF-only `loadLS("gfi-invoices")`
   read — SweetLeafPortal.js passes its own in-memory SL invoices array
   here so the SAME row-building logic (and therefore the same Total
   row / Advance-Balance badges / Previous Activity sub-rows in
   buildPaymentConfirmationHTML) works for SL deposits too, instead of
   silently returning zero rows because SL invoices live under a
   different key ("gfi-sl-invoices", not "gfi-invoices"). Omit `opts`
   (or opts.invoices) and this behaves exactly as before — GF callers
   are unaffected. */
export function partyAndRowsForInvoiceDeposit(payment, opts) {
  var invoices = (opts && opts.invoices) || loadLS("gfi-invoices", []);
  var clients = (opts && opts.clients) || loadLS("gfi-clients", []);
  var shipments = (opts && opts.shipments) || loadLS("gfi-shipments", []);
  /* SweetLeafPortal/SLPaymentsRegistry already pass their own freshly-
     loaded SL invoices as `invoices` (opts.brand === "sl") — reuse that
     same array for the Passing lookup instead of a second, possibly
     stale loadLS("gfi-sl-invoices") read. */
  var slPassingIndex = buildSlPassingIndex((opts && opts.brand === "sl") ? invoices : loadLS("gfi-sl-invoices", []));
  var firstInv = invoices.find(function (i) { return i.id === (payment.allocations[0] && payment.allocations[0].invoiceId); });
  var cli = firstInv ? clients.find(function (c) { return String(c.id) === String(firstInv.clientId); }) : null;
  var party = {
    name: (firstInv && firstInv.clientName) || (cli && (cli.fullName || cli.shortName || cli.name)) || "—",
    lines: clientAddressLines(cli),
    brand: (opts && opts.brand) || "",
  };
  var rows = (payment.allocations || []).map(function (alloc) {
    var inv = invoices.find(function (i) { return i.id === alloc.invoiceId; });
    if (!inv) return null;
    var total = calcInvoiceTotal(inv);
    var paidNow = parseFloat(inv.amountPaid) || 0;
    var prev = (inv.payments || [])
      .filter(function (p) { return p.paymentId !== payment.id; })
      .map(function (p) { return { date: p.date, amount: p.amount, method: p.method, reference: p.reference, concept: p.concept || "" }; });
    return {
      invoiceDate: inv.date,
      reference: buildInvRef(inv),
      invoiceTotal: total,
      amountPaid: alloc.amount,
      stillOwing: total - paidNow,
      concept: alloc.concept || "",
      productStatus: invoiceProductStatusDetail(inv, shipments, slPassingIndex),
      previousPayments: prev,
    };
  }).filter(Boolean);
  return { party: party, rows: rows };
}

/* Compose (party, rows) for an outbound Expense Bill payment. */
export function partyAndRowsForExpensePayment(payment) {
  var ebills = loadLS("gfi-expense-bills", []);
  /* 2026-09-15 — Arturo: "el vendor solo aparece su nombre y no su
     direccion" — unlike Bill/Invoice payments (matched by supplierId),
     ExpenseBillRegistry.js stores the vendor as a NAME with no id
     carried on the bill/allocation, so the matching record — and its
     address — has to be resolved by name. First attempt only searched
     gfi-suppliers and still came up empty for a real vendor (Godinez
     International) because ExpenseBillRegistry.js's own vendor picker
     (allVendorOptions) actually draws from FOUR catalogs — suppliers,
     clients, warehouses, AND brokers (a customs broker is a very
     common "vendor" for a service payment) — Godinez lives in
     gfi-brokers, not gfi-suppliers. Search the same four catalogs, in
     the same order, so any vendor the picker could have offered
     resolves here too. Field names (address/colony/city/state/zip/
     country) are consistent across all four catalogs' records, so
     supplierAddressLines works unmodified regardless of which one the
     match came from. */
  var vendorName = payment.shipperName || (payment.allocations[0] && payment.allocations[0].vendor) || "";
  var vendorTarget = String(vendorName).toLowerCase().trim();
  var vendorSup = null;
  if (vendorTarget) {
    ["gfi-suppliers", "gfi-clients", "gfi-warehouses", "gfi-brokers", "gfi-quality-control", "gfi-certifiers"].some(function (key) {
      var match = loadLS(key, []).find(function (s) { return String(s.shortName || s.fullName || s.name || s.companyName || "").toLowerCase().trim() === vendorTarget; });
      if (match) { vendorSup = match; return true; }
      return false;
    });
  }
  var party = {
    name: vendorName || "—",
    lines: supplierAddressLines(vendorSup),
    /* 2026-09-15 — Arturo: "los expense bill son pagos por servicios,
       no es necesario que los pdf printouts lleven [el Market
       Conditions Notice] porque no se paga fruta" — that notice exists
       specifically to caveat fruit settlement amounts against market
       volatility; a service payment (warehouse, customs, etc.) has no
       such variability. Read by buildPaymentConfirmationHTML below to
       skip the notice for this party only — bill/invoice (fruit)
       payments are untouched. */
    isExpense: true,
  };
  var rows = (payment.allocations || []).map(function (alloc) {
    var b = ebills.find(function (x) { return x.id === alloc.expenseBillId; });
    if (!b) return null;
    var total = parseFloat(b.amount) || 0;
    var paidNow = (b.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
    var prev = (b.payments || [])
      .filter(function (p) { return p.paymentId !== payment.id; })
      .map(function (p) { return { date: p.date, amount: p.amount, method: p.method, reference: p.reference, concept: p.concept || "" }; });
    return {
      invoiceDate: b.date,
      reference: buildExpenseRef(b),
      invoiceTotal: total,
      amountPaid: alloc.amount,
      stillOwing: total - paidNow,
      concept: alloc.concept || "",
      previousPayments: prev,
    };
  }).filter(Boolean);
  return { party: party, rows: rows };
}
